import { observer } from "mobx-react";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import type {
    FilterKey,
    IApplicationFilterItem,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import type { IApplicationFilterComponent } from "./IApplicationFilterComponent";
import { StatusTagChip, useStatusTagVisible } from "components/UI/StatusTags";
import { Grid } from "@mui/material";
import React from "react";
import { FormLabel, IconButton, Tooltip } from "@mui/material";
import { Close, InfoOutlined } from "@mui/icons-material";
import { useStore } from "utils/useStore";
import { FilterComponent } from "./FilterComponent";
import { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import PatientJourneyStagesDialog from "components/UI/PatientJourneyStagesDialog";
import {
    getExtendedMetadataKey,
    isGroupableExtendedMetadataField,
} from "utils/extendedMetadata";

interface ApplicationFilterItemProps
    extends IApplicationFilterItem,
        IApplicationFilterComponent {
    instanceStore: ApplicationFiltersInstanceStore;
    isClearable?: boolean;
    forceFullWidth?: boolean;
}

const ApplicationFilterItem = observer((props: ApplicationFilterItemProps) => {
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
    const filterKey = props.filterKey;

    // Label is managed by the filter component. Due to re-using old components,
    // re-factor will come another day so we don't have to do these shenanigans
    const isLabelManaged =
        filterKey === "rbcFilterItems" ||
        filterKey === "outlineSegmentFilterItems" ||
        filterKey === "wordsAndPhrasesSearchString" ||
        filterKey === "modelResults" ||
        // Module Scores puts its title on the same row as the control that adds a module, the way
        // Classifiers does, so the label belongs to the filter rather than to this wrapper.
        filterKey === "moduleScores";

    const isFullWidth =
        !!props.forceFullWidth ||
        [
            "modelResults",
            "safetyEvent",
            "moduleScores",
            "questionResults",
        ].includes(filterKey);

    const extendedMetadataKey = getExtendedMetadataKey(filterKey);
    const extendedMetadataField =
        applicationFilterItemsStore.extendedMetadataFields.find(
            (item) => item.name === extendedMetadataKey,
        );
    const isGroupableExtendedMetadata = isGroupableExtendedMetadataField(
        applicationFilterItemsStore.extendedMetadataFields,
        filterKey,
    );

    const isClearable = props.isClearable === undefined || props.isClearable;
    const isStatusTagVisible = useStatusTagVisible(props.status);

    return (
        <Grid container direction="column">
            <Grid
                container
                item
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Grid
                    container
                    item
                    direction="row"
                    spacing={1}
                    width="fit-content"
                    alignItems="center"
                >
                    <Grid item>
                        <FormLabel htmlFor={filterKey}>
                            {!isLabelManaged && props.title}
                        </FormLabel>
                    </Grid>
                    {/* Gate the wrapper too, not just the chip: the parent Grid uses
                        spacing={1}, so an empty item would leave a visible gap. */}
                    {!isLabelManaged && isStatusTagVisible && props.status && (
                        <Grid item>
                            <StatusTagChip {...props.status} />
                        </Grid>
                    )}
                    {!isLabelManaged && props.infoText && (
                        <Grid item>
                            <Tooltip title={props.infoText} arrow>
                                <InfoOutlined
                                    fontSize="small"
                                    color="action"
                                    sx={{ display: "block" }}
                                />
                            </Tooltip>
                        </Grid>
                    )}
                    {filterKey === "patientJourneyStages" && (
                        <Grid item>
                            <PatientJourneyStagesDialog />
                        </Grid>
                    )}
                </Grid>
                {isClearable && (
                    <Grid item>
                        <IconButton
                            id="remove-filter"
                            size="small"
                            onClick={() => {
                                applicationFilterItemsStore.removeSelectedFilterItem(
                                    props,
                                );

                                if (filterKey === "callDuration") {
                                    props.store.setCallDuration([]);
                                } else if (filterKey === "messageCount") {
                                    props.store.setMessageCount([]);
                                } else if (filterKey === "dateRange") {
                                    // dont remove from quickFiltersStore, rather the instanceStore.
                                    props.instanceStore.removeDateRange();
                                } else {
                                    props.store.resetByKey(filterKey);
                                }
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Grid>
                )}
            </Grid>
            <Grid container item sx={{ width: isFullWidth ? "100%" : "50%" }}>
                <Grid
                    item
                    flexGrow={1}
                    sx={{ maxWidth: isFullWidth ? undefined : "500px" }}
                >
                    <FilterComponent
                        isGroupableExtendedMetadata={
                            isGroupableExtendedMetadata
                        }
                        classifierId={
                            isGroupableExtendedMetadata
                                ? extendedMetadataField?.id
                                : undefined
                        }
                        id={filterKey}
                        store={props.store}
                        onClear={
                            filterKey === "dateRange" && isClearable
                                ? () => {
                                      applicationFilterItemsStore.removeSelectedFilterItem(props);
                                      // The editor can display quick-filter dates while saving a separate draft.
                                      props.instanceStore.removeDateRange();
                                  }
                                : undefined
                        }
                        optionRestriction={props.optionRestriction}
                        modeRestriction={props.modeRestriction}
                        rangeRestriction={props.rangeRestriction}
                        moduleScoreRestriction={props.moduleScoreRestriction}
                        restrictedValues={props.restrictedValues}
                        filterKey={
                            isGroupableExtendedMetadata
                                ? (extendedMetadataKey as FilterKey)
                                : filterKey
                        }
                    />
                </Grid>
            </Grid>
        </Grid>
    );
});

export default ApplicationFilterItem;
